import type { Key } from "@react-types/shared";
import {
  type MapboxAddressAutocompleteOption,
  type MapboxFilters,
  type MapboxGeocodedAddress,
  toMapboxAutocompleteOption,
  useMapboxGeocodeQuery,
} from "dsl/molecules/MapboxAddressInput/mapboxAddressCore";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, type AutocompleteProps } from "../autocomplete";

const defaultRenderItem = (
  item: MapboxAddressAutocompleteOption,
): { node: string; textValue: string } => ({
  node: item.label,
  textValue: item.label,
});

export type MapboxAddressAutocompleteProps = Omit<
  AutocompleteProps<MapboxAddressAutocompleteOption>,
  | "children"
  | "defaultFilter"
  | "items"
  | "onSelectionChange"
  | "renderItem"
  | "selectedKey"
> & {
  debounceMs?: number;
  mapboxFilters?: MapboxFilters;
  onSelectedOptionChange?: (
    option: MapboxAddressAutocompleteOption | null,
  ) => void;
  renderItem?: AutocompleteProps<MapboxAddressAutocompleteOption>["renderItem"];
  selectedOption?: MapboxAddressAutocompleteOption | null;
};

/**
 * Rehydrates `profile._location` (plain display string) the same way zipcode
 * uses {@link zipcodeOptionFromStoredValue}: `id` / `value` / `label` align so
 * `selectedKey` stays stable.
 */
export function mapboxAddressOptionFromLocationLabel(
  raw: unknown,
): MapboxAddressAutocompleteOption | null {
  if (raw == null || raw === "") {
    return null;
  }
  if (typeof raw !== "string") {
    return null;
  }
  const label = raw;
  const address: MapboxGeocodedAddress = {
    city: "",
    country: "",
    house_number: "",
    id: label,
    latitude: "",
    longitude: "",
    region: "",
    street: "",
    zipcode: "",
  };
  return {
    address,
    id: label,
    label,
    value: label,
  };
}

/**
 * Mapbox forward-geocode line using the design-system {@link Autocomplete}
 * (HeroUI), aligned with {@link AlgoliaAutocomplete} interaction patterns.
 *
 * **React Hook Form:** use {@link ControlledMapboxAddressAutocomplete}.
 */
export function MapboxAddressAutocomplete({
  debounceMs = 350,
  errorMessage: errorMessageFromProps,
  isInvalid: isInvalidFromProps,
  mapboxFilters,
  onClear: onClearFromAutocomplete,
  onInputChange: onInputChangeFromProps,
  onSelectedOptionChange,
  renderItem: renderItemFromProps,
  selectedOption,
  ...autocompleteProps
}: MapboxAddressAutocompleteProps) {
  const [debouncedQuery, setDebouncedQuery] = useState<string | null>(null);

  const { error, isLoading, options } = useMapboxGeocodeQuery({
    mapboxFilters,
    searchText: debouncedQuery,
  });

  const setDebouncedQueryRef = useRef(setDebouncedQuery);
  setDebouncedQueryRef.current = setDebouncedQuery;

  const debouncedSetQuery = useMemo(
    () =>
      debounce((q: string) => {
        setDebouncedQueryRef.current(q.trim() === "" ? null : q);
      }, debounceMs),
    [debounceMs],
  );

  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);

  const items = useMemo(
    () => options.map((row) => toMapboxAutocompleteOption(row)),
    [options],
  );

  const itemsForList = useMemo(() => {
    if (
      selectedOption &&
      !items.some((i) => String(i.value) === String(selectedOption.value))
    ) {
      return [selectedOption, ...items];
    }
    return items;
  }, [items, selectedOption]);

  const handleInputChange = useCallback(
    (value: string) => {
      onInputChangeFromProps?.(value);
      debouncedSetQuery(value);
    },
    [debouncedSetQuery, onInputChangeFromProps],
  );

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        onSelectedOptionChange?.(null);
        return;
      }
      const keyStr = String(key);
      const found = itemsForList.find(
        (i) => String(i.id) === keyStr || String(i.value) === keyStr,
      );
      onSelectedOptionChange?.(found ?? null);
      // After a pick, drop async hits so the list is empty; with the default
      // HeroUI `menuTrigger="focus"`, refocus would otherwise reopen the menu
      // (DEV-20544 — home care address field).
      if (found != null) {
        debouncedSetQuery.cancel();
        setDebouncedQuery(null);
      }
    },
    [debouncedSetQuery, itemsForList, onSelectedOptionChange],
  );

  const renderItem = renderItemFromProps ?? defaultRenderItem;

  const mergedErrorMessage = errorMessageFromProps ?? error?.message;
  const mergedIsInvalid = Boolean(isInvalidFromProps ?? mergedErrorMessage);

  return (
    <Autocomplete<MapboxAddressAutocompleteOption>
      {...autocompleteProps}
      allowsEmptyCollection
      defaultFilter={() => true}
      errorMessage={mergedErrorMessage}
      isInvalid={mergedIsInvalid}
      isLoading={isLoading}
      items={itemsForList}
      menuTrigger="input"
      renderItem={renderItem}
      selectedKey={selectedOption?.value ?? null}
      onClear={() => {
        onClearFromAutocomplete?.();
        debouncedSetQuery.cancel();
        setDebouncedQuery(null);
        onSelectedOptionChange?.(null);
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
    />
  );
}

MapboxAddressAutocomplete.displayName = "Recare.MapboxAddressAutocomplete";

export function mapboxGeocodedAddressToAutocompleteOption(
  row: MapboxGeocodedAddress,
): MapboxAddressAutocompleteOption {
  return toMapboxAutocompleteOption(row);
}
